import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const AdminTabs = ({ tabs, activeTab, onTabChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  // Check screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const activeTabData = tabs.find((t) => t.id === activeTab);

  // Scroll active tab into view
  useEffect(() => {
    if (scrollContainerRef.current && !isMobile) {
      const activeButton = scrollContainerRef.current.querySelector(
        `button[data-tab="${activeTab}"]`
      );
      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeTab, isMobile]);

  if (isMobile) {
    // Mobile: Dropdown style
    return (
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] hover:bg-white/10 transition-colors"
        >
          <div className="flex items-center gap-3">
            {activeTabData?.icon && <activeTabData.icon className="w-5 h-5 text-[#D4AF7A]" />}
            <span className="font-medium">{activeTabData?.name || 'Select Tab'}</span>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-[#D4AF7A] transition-transform ${
              showDropdown ? 'rotate-180' : ''
            }`}
          />
        </button>

        {showDropdown && (
          <div className="absolute z-50 w-full mt-2 bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-lg shadow-xl overflow-hidden">
            <div className="max-h-[300px] overflow-y-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      onTabChange(tab.id);
                      setShowDropdown(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                      activeTab === tab.id
                        ? 'bg-[#D4AF7A] text-black'
                        : 'text-[#F7F5F2]/80 hover:bg-white/10'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-black' : 'text-[#D4AF7A]'}`} />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop: Scrollable tabs
  return (
    <div className="relative">
      {/* Left fade indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#0A0A0A] to-transparent pointer-events-none z-10" />

      {/* Tabs */}
      <div
        ref={scrollContainerRef}
        className="flex gap-2 -mb-px overflow-x-auto hide-scrollbar px-2"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-tab={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all font-medium whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#D4AF7A] text-[#D4AF7A]'
                  : 'border-transparent text-[#F7F5F2]/60 hover:text-[#F7F5F2] hover:border-[#D4AF7A]/30'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          );
        })}
      </div>

      {/* Right fade indicator */}
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0A0A0A] to-transparent pointer-events-none z-10" />
    </div>
  );
};

export default AdminTabs;
